import type { User } from '@lmc/ts-common'
import requests from 'api'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { loadList } from 'operations/Data'
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from 'react-query'
import { useDispatch } from 'react-redux'

type UseUserProps = {
  id?: string | undefined
  includes?: Array<string> | undefined
  options?: Pick<UseQueryOptions<{ result: User }>, 'onSuccess'>
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  onCreateSuccess?: () => void
  onErrorCustom?: (e: unknown) => void
}

export type UpdateUserReturnType =
  | {
      result: User
    }
  | unknown

type UseUserReturn = {
  user: User | undefined
  isLoading: boolean
  isLoadingUpdate: boolean
  isLoadingDelete: boolean
  isLoadingCreate: boolean
  refetch: () => void
  updateUser: (values: Partial<User>) => Promise<UpdateUserReturnType>
  createUser: (values: Partial<User>) => Promise<UpdateUserReturnType>
  deleteUser: () => Promise<unknown>
}

export const SEND_USER_URL = 'list/carers'
const USER_QUERY_KEY = 'user'

const useUser = ({
  id,
  includes = [],
  options,
  onUpdateSuccess,
  onDeleteSuccess,
  onCreateSuccess,
  onErrorCustom
}: UseUserProps): UseUserReturn => {
  const dispatch = useDispatch()

  const { data, isLoading, refetch } = useQuery<
    { result: User },
    [string, string, string[]]
  >(
    [USER_QUERY_KEY, id, includes],
    ({ queryKey }) => {
      const [, id, includes] = queryKey as [string, string, string[]]
      return requests.users.fetch({ params: { id, includes } })
    },
    {
      onSuccess: options?.onSuccess,
      enabled: Boolean(id) && id?.length === 24
    }
  )

  const handleError = (err: unknown) => {
    if (onErrorCustom) {
      onErrorCustom(err)
    }
    showToast({
      title: err as string,
      type: 'error'
    })
  }

  const updateFunction = async (
    values: Partial<User>
  ): Promise<UpdateUserReturnType> =>
    postJSON({
      url: `${SEND_USER_URL}/${id}`,
      method: 'PUT',
      json: values
    })

  const deleteFunction = () =>
    postJSON({
      url: `${SEND_USER_URL}/${id}`,
      method: 'DELETE'
    })

  const { mutateAsync: updateMutation, isLoading: isLoadingUpdate } =
    useMutation<UpdateUserReturnType, unknown, Partial<User>, unknown>(
      [`update-user-${id}`],
      updateFunction,
      {
        onSuccess: () => {
          dispatch(loadList('users') as any)
          onUpdateSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const { mutateAsync: deleteMutation, isLoading: isLoadingDelete } =
    useMutation([`delete-user-${id}`], deleteFunction, {
      onSuccess: () => {
        dispatch(loadList('users') as any)
        onDeleteSuccess?.()
      },
      onError: (err) => handleError(err)
    })

  const user = data?.result

  const queryClient = useQueryClient()

  const handleUpdate = async (values: Partial<User>) => {
    try {
      const result = await updateMutation(values)
      queryClient.invalidateQueries(USER_QUERY_KEY)
      return result
    } catch (err) {
      return err
    }
  }

  const createFunction = async (
    values: Partial<User>
  ): Promise<UpdateUserReturnType> =>
    postJSON({
      url: `${SEND_USER_URL}`,
      method: 'POST',
      json: values
    })

  const { mutateAsync: createMutation, isLoading: isLoadingCreate } =
    useMutation<UpdateUserReturnType, unknown, Partial<User>, unknown>(
      [`create-user-${id}`],
      createFunction,
      {
        onSuccess: () => {
          dispatch(loadList('users') as any)
          onCreateSuccess?.()
        },
        onError: (err) => {
          handleError(err)
        }
      }
    )

  return {
    user,
    isLoading,
    isLoadingUpdate,
    isLoadingDelete,
    isLoadingCreate,
    refetch,
    updateUser: handleUpdate,
    deleteUser: deleteMutation,
    createUser: createMutation
  }
}

export const convertFormTypeToUserBody = <FormType extends object>(
  values: FormType,
  converter: (formTypeValues: FormType) => Partial<User>
): Partial<User> => {
  const convertedFormType = converter(values)

  return convertedFormType
}

export default useUser
